/* eslint-disable max-lines */
import { captureError } from '@smg-real-estate/browser-rum';
import { type HgCurrencyInputModel } from '@smg-real-estate/domus';
import {
  Address,
  AreaThePriceRefersTo,
  Attachment,
  Category,
  Characteristics,
  ContactForm,
  Country,
  GeoCoordinates,
  Lister,
  Listing,
  Localization,
  OfferType,
  Person,
  PriceInterval,
  RankType,
  Size,
  Url,
  ValueAddedServices,
} from '@smg-real-estate/hg-rets-schema';
import { type ActionContext } from 'vuex';

import {
  getUnifiedInsertionById,
  patchInsertionStatus,
  postInsertion,
  postInsertionCustomerSegment,
  postInsertionSubmittal,
  putInsertion,
  putInsertionFurthestPage,
} from '@/api/insertions';
import type { UserDetails } from '@/api/user';
import {
  setListingWebsiteLink,
  setListingYoutubeLink,
  setVirtualTourLink,
} from '@/helpers/ListingAttachment/ListingAttachment';
import { clearInapplicableCharacteristics } from '@/helpers/ListingFeatureMappings/featureMappingBusiness';
import { getCurrentDateInHgRetsFormat, mapToDotFormattedDate } from '@/helpers/dateUtils';
import { isCommercialObject } from '@/helpers/isCommercialObject';
import { objectTypeMappings } from '@/helpers/objectTypeMappings';
import { applyProjectBoostToNewConstructionData } from '@/helpers/projectBoost';
import { isDateFormat } from '@/helpers/validations';
import { ApiCallStatus, getMessageForStatus } from '@/store/insertions/InsertionsStore';
import { useAgencyStore } from '@/stores/agency/useAgencyStore';
import { useUserStore } from '@/stores/user/useUserStore';
import {
  AvailableFrom,
  Currency,
  CustomerSegment,
  InsertionStatus,
  PagesFlowOrder,
  type Insertion,
  type QueryData,
} from '@/types';

export function getInitialState(): Insertion {
  return {
    createdAt: new Date(),
    id: '',
    status: InsertionStatus.Draft,
    userId: '',
    objectReference: '',
    username: '',
    lastSavedHash: '',
    lastSavedAt: undefined,
    directPaymentInfo: '',
    customerSegment: CustomerSegment.PROFESSIONAL,
    furthestPage: '',
    shouldHideAddress: undefined,
    listing: {
      id: '',
      externalIds: {
        propertyReferenceId: '',
        refObject: '',
        refHouse: '',
        refProperty: '',
      },
      offerType: OfferType.Rent,
      availableFrom: getCurrentDateInHgRetsFormat(),
      categories: [],
      address: {
        street: '',
        postalCode: '',
        locality: undefined,
        country: Country.Ch,
        region: undefined,
      },
      characteristics: {
        numberOfRooms: undefined,
        numberOfBathrooms: undefined,
        floor: undefined,
        lotSize: undefined,
        ceilingHeight: undefined,
        cubage: undefined,
        yearBuilt: undefined,
        yearLastRenovated: undefined,
        numberOfFloors: undefined,
        totalFloorSpace: undefined,
        livingSpace: undefined,
        isWheelchairAccessible: undefined,
        isChildFriendly: undefined,
        arePetsAllowed: undefined,
        isSmokingAllowed: undefined,
        hasBalcony: undefined,
        hasGarage: undefined,
        hasParking: undefined,
        hasElevator: undefined,
        hasFireplace: undefined,
        hasWashingMachine: undefined,
        hasCableTv: undefined,
        hasNiceView: undefined,
        isQuiet: undefined,
        isMinergieCertified: undefined,
        isNewBuilding: undefined,
        isOldBuilding: undefined,
        floorLoad: undefined,
        craneCapacity: undefined,
        elevatorCapacity: undefined,
      },
      contactForm: {
        size: Size.NoAddress,
      },
      valueAddedServices: {
        bundle: '',
      },
      lister: {
        name: undefined,
        email: undefined,
        phone: undefined,
        billing: undefined,
        contacts: {
          viewing: {
            phone: undefined,
            givenName: undefined,
            note: undefined,
          },
          inquiry: {
            givenName: undefined,
            familyName: undefined,
          },
        },
      },
      localization: {
        de: {
          text: {
            description: '',
            title: '',
          },
          attachments: [],
          urls: [],
        },
        primary: 'de',
      },
      prices: {
        currency: Currency.CHF,
        rent: {
          extra: undefined,
          net: undefined,
          gross: undefined,
          interval: undefined,
          area: undefined,
        },
        buy: {
          price: undefined,
        },
      },
    },
  };
}

const getOfferTypeFromQueryData = (offer: string, listingOfferType?: OfferType): OfferType => {
  if (!listingOfferType) {
    return offer === 'SALE' ? OfferType.Buy : OfferType.Rent;
  }
  return listingOfferType;
};

const getCategoriesFromQueryData = (type: string): Category[] => {
  return objectTypeMappings[type?.toUpperCase()];
};

const cleanupLocalizationUrls = (
  localization: Localization | undefined,
): Localization | undefined => {
  if (!localization) {
    return undefined;
  }

  if (!localization.de || !localization.de.urls) {
    return localization;
  }

  return {
    ...localization,
    de: {
      ...localization.de,
      urls: localization.de.urls.map((item: any) => {
        Object.keys(item).forEach((key) => {
          if (!item[key]) {
            delete item[key];
          }
        });
        return item;
      }),
    },
  };
};

const ensureViewingContact = (state: Insertion): Person => {
  if (!state.listing.lister) {
    state.listing.lister = {};
  }
  if (!state.listing.lister.contacts) {
    state.listing.lister.contacts = {};
  }
  if (!state.listing.lister.contacts.viewing) {
    state.listing.lister.contacts.viewing = {};
  }
  return state.listing.lister.contacts.viewing;
};

const getters = {
  getInsertion: (state: Insertion): Insertion => state,
  getInsertionListing: (state: Insertion): Listing => state.listing,
  getInsertionListingId: (state: Insertion): string | null | undefined => state.listing.id,
  getInsertionId: (state: Insertion): string => state.id,
  getInsertionStatus: (state: Insertion): InsertionStatus => state.status,
  getShouldHideAddress: (state: Insertion): boolean | undefined => state.shouldHideAddress,
  getPriceWithCurrency: (state: Insertion): HgCurrencyInputModel => {
    let price;
    if (OfferType.Rent === state.listing.offerType) {
      price = state.listing.prices?.rent?.net || state.listing.prices?.rent?.gross;
    }
    if (OfferType.Buy === state.listing.offerType) {
      price = state.listing.prices?.buy?.price;
    }
    return {
      price,
      currency: state.listing.prices?.currency ?? Currency.CHF,
    } as HgCurrencyInputModel;
  },
  getAvailableFromDate: (state: Insertion): string | undefined => {
    if (state.listing.availableFrom !== undefined) {
      const formattedDate = mapToDotFormattedDate(state.listing.availableFrom);
      return isDateFormat(formattedDate) ? formattedDate : state.listing.availableFrom;
    }
    return undefined;
  },
  getAvailableFromType: (state: Insertion): AvailableFrom => {
    if (state.listing.availableFrom !== undefined) {
      const formattedDate = mapToDotFormattedDate(state.listing.availableFrom);
      if (isDateFormat(formattedDate) && new Date(state.listing.availableFrom) <= new Date()) {
        return AvailableFrom.IMMEDIATELY;
      }
      return AvailableFrom.DATE;
    }
    return AvailableFrom.BY_AGREEMENT;
  },
  getListerViewingPhone: (state: Insertion): string | undefined =>
    state.listing.lister?.contacts?.viewing?.phone,
  getListerViewingName: (state: Insertion): string | undefined =>
    state.listing?.lister?.contacts?.viewing?.givenName,
  getInsertionRegion: (state: Insertion): string | undefined => {
    return state.listing?.address?.region;
  },
  getInsertionObjectReference: (state: Insertion): string | undefined => {
    return state.listing.externalIds?.propertyReferenceId;
  },
  getListingLocality: (state: Insertion): string | undefined => {
    return state.listing.address?.locality;
  },
  getListingCategories: (state: Insertion): string[] => {
    return state.listing.categories || [];
  },
  getListingTitle: (state: Insertion): string | undefined => {
    return state.listing.localization?.de?.text?.title;
  },
  getIsLifetimeTopListing: (state: Insertion): boolean => {
    return state.listing.valueAddedServices?.isLifetimeToplisting || false;
  },
  getListingAttachments: (state: Insertion): Attachment[] => {
    return state.listing.localization?.de?.attachments || [];
  },
};
const mutations = {
  setInsertion(state: Insertion, newInsertion: Insertion): void {
    Object.entries(newInsertion).forEach((entry) => {
      const [key, value] = entry;
      state[key as keyof Insertion] = value;
    });
  },
  setShouldHideAddress(state: Insertion, shouldHideAddress: boolean): void {
    state.shouldHideAddress = shouldHideAddress;
  },
  setId(state: Insertion, id: string): void {
    state.id = id;
  },
  setLastSaved(state: Insertion): void {
    state.lastSavedAt = new Date();
  },
  setListing: (state: Insertion, listing: Listing): void => {
    state.listing = listing;
  },
  setOfferType: (state: Insertion, offerType: OfferType): void => {
    if (OfferType.Rent === offerType) {
      state.listing.prices = {
        ...state.listing.prices,
        ...{
          buy: getInitialState().listing.prices?.buy,
        },
      };
    }
    if (OfferType.Buy === offerType) {
      state.listing.prices = {
        ...state.listing.prices,
        ...{
          rent: getInitialState().listing.prices?.rent,
        },
      };
    }
    state.listing.offerType = offerType;
  },
  setCategories(state: Insertion, categories: Category[]): void {
    clearInapplicableCharacteristics(state, categories);

    state.listing.categories = categories;
  },
  setPhone(state: Insertion, phone: string): void {
    (state.listing.lister as Lister).phone = phone;
  },
  setListingStreetAndNumber(state: Insertion, streetAndNumber: string): void {
    (state.listing.address as Address).street = streetAndNumber;
  },
  setListingZipCode(state: Insertion, zipCode: string): void {
    (state.listing.address as Address).postalCode = zipCode;
  },
  setListingCity(state: Insertion, city: string): void {
    (state.listing.address as Address).locality = city;
  },
  setListingCountry(state: Insertion, country: Country): void {
    (state.listing.address as Address).country = country;
  },
  setListingRegion(state: Insertion, region: string): void {
    (state.listing.address as Address).region = region;
  },
  setContactGivenName(state: Insertion, name: string): void {
    (state.listing.lister?.contacts?.inquiry as Person).givenName = name;
  },
  setContactFamilyName(state: Insertion, familyName: string): void {
    (state.listing.lister?.contacts?.inquiry as Person).familyName = familyName;
  },
  setEmail(state: Insertion, email: string): void {
    (state.listing?.lister as Lister).email = email;
  },
  setTitle(state: Insertion, value: string): void {
    (state.listing.localization?.de?.text as any).title = value;
  },
  setDescription(state: Insertion, value: string): void {
    (state.listing.localization?.de?.text as any).description = value;
  },
  setBundle(state: Insertion, bundleMatchingId: string): void {
    (state.listing.valueAddedServices as ValueAddedServices).bundle = bundleMatchingId;
  },
  setIsLifetimeTopListing(state: Insertion, isLifetimeTopListing?: boolean): void {
    (state.listing.valueAddedServices as ValueAddedServices).isLifetimeToplisting =
      isLifetimeTopListing;
  },
  setRankType(state: Insertion, payload?: { rankType: RankType; start: Date; end?: Date }): void {
    (state.listing.valueAddedServices as ValueAddedServices).rank = {
      start: payload?.start.toISOString(),
      end: payload?.end?.toISOString(),
      type: payload?.rankType,
    };
  },
  /**
   * NC Boost is part of the listing rather than a toplisting booking, so the funnel keeps it on
   * the draft and the publish request carries it, the way `setRankType` works for toplisting.
   */
  setProjectBoostSelection(state: Insertion, enabled: boolean): void {
    state.listing.newConstructionData = applyProjectBoostToNewConstructionData(
      state.listing.newConstructionData,
      enabled,
    );
  },
  setPropertyOwnerForBilling(state: Insertion, refOwner: string): void {
    const PUBLISHER_ID = 'smg';
    const KEY = 'cost-forwarding-ref-owner';

    const publishers = state.listing.publishers || []; // Default to empty array if undefined
    const publisherToUpdate = publishers.find((publisher) => publisher.id === PUBLISHER_ID);

    // Create new publisher if 'smg' doesn't exist
    if (!publisherToUpdate) {
      publishers.push({
        id: PUBLISHER_ID,
        options: [
          {
            key: KEY,
            value: refOwner,
          },
        ],
      });
    }
    // If the publisher with id 'smg' exists, check if it has an option with key 'cost-forwarding-ref-owner'
    else {
      publisherToUpdate.options = publisherToUpdate.options || []; // Default to empty array if undefined

      const optionToUpdate = publisherToUpdate.options.find((option) => option.key === KEY);

      if (!optionToUpdate) {
        // Create new option if 'cost-forwarding-ref-owner' doesn't exist
        publisherToUpdate.options?.push({ key: KEY, value: refOwner });
      } else {
        // Update existing option with new value
        optionToUpdate.value = refOwner;
      }
    }

    state.listing.publishers = publishers;
  },
  setNumberOfRooms(state: Insertion, numberOfRooms: number): void {
    (state.listing.characteristics as Characteristics).numberOfRooms = numberOfRooms;
  },
  setNumberOfBathrooms(state: Insertion, numberOfBathrooms: number): void {
    (state.listing.characteristics as Characteristics).numberOfBathrooms = numberOfBathrooms;
  },
  setLivingSpace(state: Insertion, livingSpace: number): void {
    (state.listing.characteristics as Characteristics).livingSpace = livingSpace;
  },
  setFloor(state: Insertion, floor: number): void {
    (state.listing.characteristics as Characteristics).floor = floor;
  },
  setLotSize(state: Insertion, lotSize: number): void {
    (state.listing.characteristics as Characteristics).lotSize = lotSize;
  },
  setCeilingHeight(state: Insertion, ceilingHeight: number): void {
    (state.listing.characteristics as Characteristics).ceilingHeight = ceilingHeight;
  },
  setYearBuilt(state: Insertion, yearBuilt: number): void {
    (state.listing.characteristics as Characteristics).yearBuilt = yearBuilt;
  },
  setYearLastRenovated(state: Insertion, yearLastRenovated: number): void {
    (state.listing.characteristics as Characteristics).yearLastRenovated = yearLastRenovated;
  },
  setNumberOfFloors(state: Insertion, numberOfFloors: number): void {
    (state.listing.characteristics as Characteristics).numberOfFloors = numberOfFloors;
  },
  setNumberOfApartments(state: Insertion, numberOfApartments: number): void {
    (state.listing.characteristics as Characteristics).numberOfApartments = numberOfApartments;
  },
  setCubage(state: Insertion, cubage: number): void {
    (state.listing.characteristics as Characteristics).cubage = cubage;
  },
  setTotalFloorSpace(state: Insertion, totalFloorSpace: number): void {
    (state.listing.characteristics as Characteristics).totalFloorSpace = totalFloorSpace;
  },
  setSingleFloorSpace(state: Insertion, singleFloorSpace: number): void {
    (state.listing.characteristics as Characteristics).singleFloorSpace = singleFloorSpace;
  },
  setRentPriceExtra(state: Insertion, extra: number): void {
    state.listing.prices = {
      ...state.listing.prices,
      ...{
        rent: {
          ...state.listing.prices?.rent,
          extra,
        },
      },
    };
  },
  setRentPriceNet(state: Insertion, net: number): void {
    state.listing.prices = {
      ...state.listing.prices,
      ...{
        rent: {
          ...state.listing.prices?.rent,
          net,
        },
      },
    };
  },
  setRentPriceGross(state: Insertion, gross: number): void {
    state.listing.prices = {
      ...state.listing.prices,
      ...{
        rent: {
          ...state.listing.prices?.rent,
          gross,
        },
      },
    };
  },
  setPriceInterval(state: Insertion, priceInterval: PriceInterval): void {
    state.listing.prices = {
      ...state.listing.prices,
      ...{
        rent: {
          ...state.listing.prices?.rent,
          interval: priceInterval,
        },
      },
    };
  },
  setAreaThePriceRefersTo(state: Insertion, area: AreaThePriceRefersTo): void {
    state.listing.prices = {
      ...state.listing.prices,
      ...{
        rent: {
          ...state.listing.prices?.rent,
          area,
        },
      },
    };
  },
  setPriceCurrency(state: Insertion, currency: string): void {
    state.listing.prices = {
      ...state.listing.prices,
      ...{
        currency,
      },
    };
  },
  setBuyPrice(state: Insertion, price: number): void {
    state.listing.prices = {
      ...state.listing.prices,
      ...{
        buy: {
          ...state.listing.prices?.buy,
          price,
        },
      },
    };
  },
  setGeoCoordinates(state: Insertion, geoCoordinates: GeoCoordinates): void {
    (state.listing.address as Address).geoCoordinates = {
      latitude: geoCoordinates.latitude,
      longitude: geoCoordinates.longitude,
    };
  },
  removeGeoCoordinates(state: Insertion): void {
    (state.listing.address as Address).geoCoordinates = undefined;
  },
  setStatus(state: Insertion, status: InsertionStatus): void {
    state.status = status;
  },
  setObjectReferenceId(state: Insertion, status: string): void {
    if (state.listing.externalIds) {
      state.listing.externalIds.propertyReferenceId = status;
      state.listing.externalIds.internalReferenceId = status;
    }
  },
  setAttachments(state: Insertion, value: Attachment[]): void {
    if (state.listing.localization?.de) {
      state.listing.localization.de.attachments = value;
    }
  },
  setAvailableFrom(state: Insertion, value: string): void {
    state.listing.availableFrom = value;
  },
  updateInsertionStatus(state: Insertion, status: InsertionStatus): void {
    state.status = status;
  },
  toggleListingFeature(state: Insertion, feature: string): void {
    const oldFeatureValue = (state.listing.characteristics as any)[feature];
    if (oldFeatureValue) {
      (state.listing.characteristics as Characteristics)[feature as keyof Characteristics] =
        undefined;
    } else {
      (state.listing.characteristics as Characteristics)[feature as keyof Characteristics] =
        true as any;
    }
  },
  setListerViewingName(state: Insertion, name: string): void {
    ensureViewingContact(state).givenName = name;
  },
  setListerViewingPhone(state: Insertion, phone: string): void {
    ensureViewingContact(state).phone = phone;
  },
  setViewingNote(state: Insertion, viewingNote: string): void {
    ensureViewingContact(state).note = viewingNote;
  },
  resetViewingContact(state: Insertion): void {
    const contacts = state.listing.lister?.contacts;
    if (!contacts?.viewing) {
      return;
    }
    // preserve note, but clear other fields
    const { note } = contacts.viewing;
    contacts.viewing = { note };
  },
  setContactFormType(state: Insertion, contactFormType: string): void {
    (state.listing.contactForm as ContactForm).size = contactFormType as Size;
  },
  setFurthestPage(state: Insertion, furthestPage: PagesFlowOrder): void {
    /*
    FurthestPage is stored on the Insertion as a string, so to compare it must be converted back to the PagesFlowOrder enum.
    If the new value is a higher enum than the old value, the enum key of the new value is written out as a string.
     */
    const currentFurthestPage = state.furthestPage as string;
    type PagesFlowOrderStrings = keyof typeof PagesFlowOrder;
    if (
      !currentFurthestPage ||
      furthestPage > PagesFlowOrder[currentFurthestPage as PagesFlowOrderStrings]
    ) {
      state.furthestPage = PagesFlowOrder[furthestPage];
    }
  },
  setWebsiteUrl(state: Insertion, urlData: Url): void {
    if (state.listing.localization?.de) {
      const urls = setListingWebsiteLink(state.listing.localization?.de?.urls as Url[], urlData);
      state.listing.localization.de.urls = urls;
    }
  },
  setYoutubeUrl(state: Insertion, url: string): void {
    if (state.listing.localization?.de) {
      const urls = setListingYoutubeLink(state.listing.localization?.de?.urls as Url[], url);
      state.listing.localization.de.urls = urls;
    }
  },
  setVirtualTour(state: Insertion, url: string): void {
    if (state.listing.localization?.de) {
      const urls = setVirtualTourLink(state.listing.localization?.de?.urls as Url[], url);
      state.listing.localization.de.urls = urls;
    }
  },
  removeListerAddressAndNames(state: Insertion): void {
    (state.listing.lister as Lister).address = undefined;
    (state.listing.lister as Lister).name = undefined;
    (state.listing.lister as Lister).legalName = undefined;
  },
  setListerName(state: Insertion, name: string): void {
    (state.listing.lister as Lister).name = name;
  },
  setListerLegalName(state: Insertion, legalName: string): void {
    (state.listing.lister as Lister).legalName = legalName;
  },
  setListerStreetAndNumber(state: Insertion, streetAndNumber: string): void {
    (state.listing.lister as Lister).address = {
      ...state.listing.lister?.address,
      street: streetAndNumber,
    };
  },
  setListerCountry(state: Insertion, country: Country): void {
    (state.listing.lister as Lister).address = {
      ...state.listing.lister?.address,
      country,
    };
  },
  setListerZipCode(state: Insertion, postalCode: string): void {
    (state.listing.lister as Lister).address = {
      ...state.listing.lister?.address,
      postalCode: postalCode.toString(),
    };
  },
  setListerCity(state: Insertion, city: string): void {
    (state.listing.lister as Lister).address = {
      ...state.listing.lister?.address,
      locality: city,
    };
  },
  setFloorLoad(state: Insertion, floorLoad: number): void {
    (state.listing.characteristics as Characteristics).floorLoad = floorLoad;
  },
  setCraneCapacity(state: Insertion, craneCapacity: number): void {
    (state.listing.characteristics as Characteristics).craneCapacity = craneCapacity;
  },
  setElevatorCapacity(state: Insertion, elevatorCapacity: number): void {
    (state.listing.characteristics as Characteristics).elevatorCapacity = elevatorCapacity;
  },
};

const actions = {
  async setInsertion(context: ActionContext<Insertion, any>, insertion: Insertion): Promise<void> {
    context.commit('setInsertion', insertion);
    await context.dispatch('seon/configure', {
      insertionId: insertion.id,
      userId: insertion.userId,
    });
  },
  clearInsertionState(context: ActionContext<Insertion, any>): void {
    context.commit('setInsertion', getInitialState());
    context.dispatch('seon/clear');
  },
  initializeNewInsertion(
    context: ActionContext<Insertion, any>,
    {
      offer,
      type,
      country,
      city,
      zip,
      street,
      canton,
      listingOfferType,
      listingCategoriesMapped,
    }: QueryData,
  ): void {
    const offerType: OfferType = getOfferTypeFromQueryData(offer, listingOfferType);
    const categories: Category[] = listingCategoriesMapped || getCategoriesFromQueryData(type);

    context.commit('setOfferType', offerType);
    context.commit('setCategories', categories);
    context.commit('setListingStreetAndNumber', street);
    context.commit('setListingZipCode', zip);
    context.commit('setListingCity', city);
    context.commit('setListingCountry', country);
    context.commit('setListingRegion', canton);
    context.commit('setFurthestPage', PagesFlowOrder.DETAILS);

    if (offerType === OfferType.Rent) {
      context.commit('setPriceInterval', PriceInterval.Month);
      if (isCommercialObject(categories)) {
        context.commit('setAreaThePriceRefersTo', AreaThePriceRefersTo.All);
      }
    }
  },

  async fetchInsertionById(
    context: ActionContext<Insertion, any>,
    payload: { userId: string; insertionId: string },
  ): Promise<void> {
    const sourcePlatform = useUserStore().sourcePlatform;
    const insertion = await getUnifiedInsertionById(
      payload.userId,
      payload.insertionId,
      sourcePlatform,
    );

    // Temporary fix to remove unwanted null values from urls.
    // See also https://homegate.atlassian.net/browse/HOM-1569
    insertion.listing.localization = cleanupLocalizationUrls(insertion.listing.localization);
    insertion.listing.characteristics = insertion.listing.characteristics || {};

    await context.dispatch('setInsertion', insertion);
    context.commit('clearImageDescriptions');
  },
  updateListingStreetAndNumber(
    context: ActionContext<Insertion, any>,
    streetAndNumber: string,
  ): void {
    context.commit('setListingStreetAndNumber', streetAndNumber);
  },
  updateListingZipAndCity(context: ActionContext<Insertion, any>, zipAndCity: string): void {
    const split = zipAndCity.split(',', 2);
    context.commit('setListingZipCode', split[0].trim());
    context.commit('setListingCity', split[1].trim());
  },
  updateRentPricesWithAdditionalCosts(
    context: ActionContext<Insertion, any>,
    { extra, extraIncluded }: { extra?: number; extraIncluded?: boolean },
  ): void {
    const price = context.getters.getPriceWithCurrency.price;
    const priceExtra = extra ? Math.round(extra) : undefined;
    context.commit('setRentPriceExtra', priceExtra);
    context.dispatch('updatePrice', { price, priceExtra, extraIncluded });
  },
  updatePriceWithCurrency(
    context: ActionContext<Insertion, any>,
    data: HgCurrencyInputModel & { extraIncluded?: boolean },
  ): void {
    context.commit('setPriceCurrency', data.currency);

    if (OfferType.Rent === context.state.listing.offerType) {
      const price = data.price ? Math.round(data.price) : undefined;
      const priceExtra = context.state.listing.prices?.rent?.extra;
      context.dispatch('updatePrice', { price, priceExtra, extraIncluded: data.extraIncluded });
    }

    if (OfferType.Buy === context.state.listing.offerType) {
      context.commit('setBuyPrice', data.price);
    }
  },
  updatePrice(
    { commit }: ActionContext<Insertion, any>,
    {
      price,
      priceExtra,
      extraIncluded,
    }: { price: number; priceExtra?: number; extraIncluded?: boolean },
  ): void {
    if (price && !extraIncluded) {
      const grossPrice = priceExtra ? price + priceExtra : undefined;
      commit('setRentPriceNet', price);
      commit('setRentPriceGross', grossPrice);
    } else {
      commit('setRentPriceNet', undefined);
      commit('setRentPriceGross', price);
    }
  },
  async createNewInsertion(
    context: ActionContext<Insertion, any>,
    queryData: QueryData,
  ): Promise<string> {
    await context.dispatch('initializeNewInsertion', queryData);
    const userId = useAgencyStore().activeAgencyPersonId as string;
    const sourcePlatform = useUserStore().sourcePlatform;
    const insertion = await postInsertion(userId, context.state.listing, sourcePlatform);

    putInsertionFurthestPage(
      userId,
      insertion.id,
      PagesFlowOrder[PagesFlowOrder.DETAILS],
      sourcePlatform,
    ).catch(captureError);

    postInsertionCustomerSegment(userId, insertion.id, sourcePlatform).catch(captureError);

    //preserve flag
    const shouldHideAddress = context.getters.getShouldHideAddress;
    await context.dispatch('setInsertion', insertion);
    context.commit('setShouldHideAddress', shouldHideAddress);

    return insertion.id;
  },
  async saveInsertion(context: ActionContext<Insertion, any>): Promise<void> {
    const ownerId = context.state.userId;
    const insertionId = context.getters.getInsertionId;
    const sourcePlatform = useUserStore().sourcePlatform;

    context.commit('setLastSaved');
    try {
      await putInsertion(ownerId, insertionId, context.state.listing, sourcePlatform);
    } catch (error) {
      captureError(error);
    }
  },
  async saveAndSubmitForApproval(context: ActionContext<Insertion, any>): Promise<void> {
    const ownerId = context.state.userId;
    const insertionId = context.getters.getInsertionId;
    const sourcePlatform = useUserStore().sourcePlatform;
    const {
      enrolledInMFA: userEnrolledInMFA,
      requiredMFA,
      mfaEnrollmentDateUtc,
    } = useUserStore().userDetails as UserDetails;

    context.commit('setLastSaved');
    await putInsertion(ownerId, insertionId, context.state.listing, sourcePlatform);

    await context.dispatch(
      'seon/saveSeonSession',
      { ownerId, insertionId, userEnrolledInMFA, requiredMFA, mfaEnrollmentDateUtc },
      { root: true },
    );
    const insertion = await postInsertionSubmittal(ownerId, insertionId, sourcePlatform);
    context.commit('setInsertion', insertion);
    if (insertion) {
      context.dispatch('registerInsertionAfterPublish', insertion, { root: true });
    }
  },
  async changeStatus(
    context: ActionContext<Insertion, any>,
    {
      insertion,
      status,
    }: {
      insertion: Insertion;
      status: InsertionStatus;
    },
  ): Promise<void> {
    try {
      context.commit('setApiCallStatus', ApiCallStatus.IN_PROGRESS);

      const sourcePlatform = useUserStore().sourcePlatform;
      await patchInsertionStatus(insertion.userId, insertion.id, status, sourcePlatform);
      context.commit('setApiCallStatus', ApiCallStatus.SUCCESS);

      context.commit('updateInsertionStatus', status);
      context.commit('setListingActionSuccess', getMessageForStatus(status));
    } catch (error) {
      context.commit('setApiCallStatus', ApiCallStatus.ERROR);
      captureError(error);
    }
  },
  async updateFurthestPage(
    context: ActionContext<Insertion, any>,
    furthestPage: PagesFlowOrder,
  ): Promise<void> {
    const ownerId = context.state.userId;
    const insertionId = context.getters.getInsertionId;
    const sourcePlatform = useUserStore().sourcePlatform;

    /*
    FurthestPage is stored on the Insertion as a string, so to compare it must be converted back to the PagesFlowOrder enum.
    If the new value is a higher enum than the old value, the enum key of the new value is written out as a string.
     */
    const currentFurthestPage = context.state.furthestPage as string;
    type PagesFlowOrderStrings = keyof typeof PagesFlowOrder;
    if (
      !currentFurthestPage ||
      furthestPage > PagesFlowOrder[currentFurthestPage as PagesFlowOrderStrings]
    ) {
      context.commit('setFurthestPage', furthestPage);
      if (!context.state.furthestPage) {
        return;
      }
      try {
        await putInsertionFurthestPage(
          ownerId,
          insertionId,
          context.state.furthestPage,
          sourcePlatform,
        );
      } catch (error) {
        captureError(error);
      }
    }
  },
};

const _default = { state: getInitialState, getters, mutations, actions };
export default _default;
